<template>
  <div class="adoption-keys-panel__backdrop" @click.self="$emit('close')">
    <div class="adoption-keys-panel">
      <div class="adoption-keys-panel__header">
        <div>
          <h3>Adoption keys</h3>
          <p>Credentials for <code>luna adopt &lt;api-domain&gt; &lt;key&gt;</code> — the one-command self-enrolment path (mint a key below to get the exact command). Anyone holding a live key can enrol a server; revoke one the moment a box retires.</p>
        </div>
        <button type="button" class="adoption-keys-panel__close" @click="$emit('close')">&times;</button>
      </div>

      <div class="adoption-keys-panel__body">
        <div v-if="error" class="adoption-keys-panel__error">{{ error }}</div>

        <div v-if="justMinted" class="adoption-keys-panel__minted">
          <div class="adoption-keys-panel__minted-label">
            Run this on the server — copy it now, the key won't be shown again.
          </div>
          <div class="adoption-keys-panel__token-row">
            <code>{{ adoptCommand }}</code>
            <button type="button" class="adoption-keys-panel__copy" @click="copy(adoptCommand)">Copy command</button>
          </div>
          <p class="adoption-keys-panel__minted-note">
            <code>{{ apiDomain }}</code> is this install's <strong>API</strong> domain — <code>luna adopt</code> POSTs
            JSON to it, so the dashboard domain won't work. <code>--insecure</code> covers first contact only: the
            agent has no trust anchor yet; it pins the hub's certificate during enrolment and verifies it from then on.
          </p>
        </div>

        <form class="adoption-keys-panel__create" @submit.prevent="createKey">
          <input
            v-model="newLabel"
            type="text"
            placeholder="Label (e.g. &quot;Ops laptop&quot;, &quot;CI box&quot;)"
            class="adoption-keys-panel__input"
          />
          <button type="submit" class="adoption-keys-panel__btn adoption-keys-panel__btn--primary" :disabled="creating">
            {{ creating ? 'Creating…' : 'New key' }}
          </button>
        </form>

        <table class="adoption-keys-panel__table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Prefix</th>
              <th>Last used</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="5" class="adoption-keys-panel__loading">Loading…</td>
            </tr>
            <tr v-else-if="!keys.length">
              <td colspan="5" class="adoption-keys-panel__loading">No adoption keys yet.</td>
            </tr>
            <tr v-for="key in keys" :key="key.id" :class="{ 'adoption-keys-panel__row--revoked': key.revoked_at }">
              <td>{{ key.label }}</td>
              <td><code>dae_adopt_{{ key.key_prefix }}…</code></td>
              <td>{{ key.last_used_at ? relativeTime(key.last_used_at) : 'never' }}</td>
              <td>
                <span v-if="key.revoked_at" class="adoption-keys-panel__pill adoption-keys-panel__pill--revoked">Revoked</span>
                <span v-else class="adoption-keys-panel__pill adoption-keys-panel__pill--live">Live</span>
              </td>
              <td>
                <button
                  v-if="!key.revoked_at"
                  type="button"
                  class="adoption-keys-panel__revoke"
                  @click="revokeKey(key)"
                >Revoke</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import apiHost from '@root/services/apiHost';

// Staff CRUD surface for `AdoptionKey` (Plan 003 §4d, 2026-07-17 addendum) —
// backs `AdoptionKeyController` at `/api/v1/servers/adoption-keys`. Lives
// beside `ServerEnrolmentModal` in the fleet board's enrolment area (a
// "Adoption keys" button next to "Enrol a server") since both are entry
// points into getting an agent onto the fleet, just via different paths
// (staff-issued single-use token vs. a standing key the agent presents
// itself). Convention-driven CRUD table per Plan 003 §2a's design-gate note
// — no dedicated mockup needed for a surface this size.
const props = defineProps({
  apiBase: {
    type: String,
    default: '/api/v1/servers',
  },
});

defineEmits(['close']);

const api = useApi();

const keys = ref([]);
const loading = ref(false);
const error = ref(null);
const newLabel = ref('');
const creating = ref(false);
const justMinted = ref(null);

// The just-minted block renders the COMPLETE ready-to-paste command rather
// than the bare key — `luna adopt`'s `<domain>` must be the API host (the
// CLI prepends `https://` and POSTs `/api/v1/servers/adopt-requests`), and
// showing only the key next to a generic `luna adopt <domain> <key>` hint
// led real adoptions to paste the SPA domain and choke on the catch-all's
// HTML (demeter 2026-07-18, eunomia 2026-07-19). See `services/apiHost.js`.
const apiDomain = apiHost();
const adoptCommand = computed(() => `sudo luna adopt ${apiDomain} ${justMinted.value} --insecure`);

async function loadKeys() {
  loading.value = true;
  error.value = null;
  try {
    const data = await api.get(`${props.apiBase}/adoption-keys`);
    keys.value = data.keys || [];
  } catch (err) {
    error.value = 'Could not load adoption keys.';
  } finally {
    loading.value = false;
  }
}

async function createKey() {
  creating.value = true;
  error.value = null;
  try {
    const data = await api.post(`${props.apiBase}/adoption-keys`, { label: newLabel.value });
    justMinted.value = data.plaintext;
    newLabel.value = '';
    await loadKeys();
  } catch (err) {
    error.value = err?.response?.data?.error || 'Could not create a key.';
  } finally {
    creating.value = false;
  }
}

async function revokeKey(key) {
  try {
    await api.delete(`${props.apiBase}/adoption-keys/${key.id}`);
    await loadKeys();
  } catch (err) {
    error.value = err?.response?.data?.error || 'Could not revoke that key.';
  }
}

function relativeTime(epochSeconds) {
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - epochSeconds);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

function copy(text) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => {});
  }
}

onMounted(loadKeys);
</script>

<style lang="scss">
.adoption-keys-panel {
  background: var(--theme-bg-card);
  border-radius: var(--theme-radius-lg, 10px);
  box-shadow: var(--theme-shadow-panel);
  width: min(640px, 92vw);
  max-height: 88vh;
  overflow-y: auto;

  &__backdrop {
    position: fixed;
    inset: 0;
    background: var(--theme-bg-overlay, rgba(14, 46, 48, 0.65));
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 6vh 16px;
    z-index: 1000;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--theme-border-soft);
    gap: 12px;

    h3 {
      margin: 0 0 4px;
      font-size: 15px;
      font-weight: 600;
    }

    p {
      margin: 0;
      font-size: 12px;
      color: var(--theme-text-secondary);
      line-height: 1.5;
      max-width: 60ch;

      code {
        font-family: var(--theme-font-mono);
      }
    }
  }

  &__close {
    border: none;
    background: none;
    font-size: 20px;
    line-height: 1;
    color: var(--theme-text-tertiary);
    cursor: pointer;
    flex-shrink: 0;
  }

  &__body {
    padding: 16px 20px 20px;
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 8px 12px;
    font-size: 12px;
    margin-bottom: 14px;
  }

  &__minted {
    background: var(--theme-accent-light);
    border-radius: var(--theme-radius, 6px);
    padding: 12px 14px;
    margin-bottom: 16px;
  }

  &__minted-label {
    font-size: 11px;
    font-weight: 700;
    color: var(--theme-accent-dark);
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 8px;
  }

  &__minted-note {
    margin: 8px 0 0;
    font-size: 11.5px;
    line-height: 1.5;
    color: var(--theme-text-secondary);

    code {
      font-family: var(--theme-font-mono);
    }
  }

  &__token-row {
    display: flex;
    align-items: center;
    gap: 10px;
    background: var(--theme-bg-content);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;

    code {
      flex: 1;
      font-family: var(--theme-font-mono);
      font-size: 12px;
      overflow-x: auto;
      white-space: nowrap;
    }
  }

  &__copy {
    font-size: 11.5px;
    font-weight: 600;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 6px 10px;
    cursor: pointer;
    white-space: nowrap;
  }

  &__create {
    display: flex;
    gap: 10px;
    margin-bottom: 16px;
  }

  &__input {
    flex: 1;
    font-size: 12.5px;
    padding: 8px 12px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-content);
    color: var(--theme-text-primary);
  }

  &__btn {
    font-size: 12.5px;
    font-weight: 600;
    padding: 8px 14px;
    border-radius: var(--theme-radius, 6px);
    cursor: pointer;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    white-space: nowrap;

    &--primary {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
      color: #fff;
    }

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;

    th {
      text-align: left;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: .05em;
      text-transform: uppercase;
      color: var(--theme-text-tertiary);
      padding: 8px 10px;
      border-bottom: 1px solid var(--theme-border);
    }

    td {
      padding: 9px 10px;
      font-size: 12.5px;
      border-bottom: 1px solid var(--theme-border-soft);
      vertical-align: middle;

      code {
        font-family: var(--theme-font-mono);
        font-size: 11.5px;
      }
    }
  }

  &__row--revoked {
    opacity: .55;
  }

  &__loading {
    text-align: center;
    color: var(--theme-text-tertiary);
    padding: 18px !important;
  }

  &__pill {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .02em;
    padding: 2.5px 8px;
    border-radius: 3px;
    white-space: nowrap;

    &--live {
      background: var(--theme-status-success);
      color: #fff;
    }

    &--revoked {
      background: var(--theme-border-soft);
      color: var(--theme-text-tertiary);
    }
  }

  &__revoke {
    font-size: 11px;
    font-weight: 600;
    background: none;
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 5px 9px;
    cursor: pointer;
    color: var(--theme-status-danger, #a53232);
  }
}
</style>
